/**
 * Sistema de throttling para evitar spam de erros e notificações
 */

interface ThrottleEntry {
  count: number;
  lastOccurrence: number;
  firstOccurrence: number;
}

interface ThrottleOptions {
  maxOccurrences: number;
  windowMs: number;
  cooldownMs?: number;
}

class ErrorThrottlingService {
  private errorCounts: Map<string, ThrottleEntry> = new Map();
  private notificationCounts: Map<string, ThrottleEntry> = new Map();
  private readonly defaultOptions: ThrottleOptions = {
    maxOccurrences: 5,
    windowMs: 60000, // 1 minuto
    cooldownMs: 300000, // 5 minutos de cooldown
  };

  /**
   * Verifica se um erro deve ser logado/mostrado
   */
  shouldLogError(
    errorKey: string,
    options?: Partial<ThrottleOptions>
  ): boolean {
    const opts = { ...this.defaultOptions, ...options };
    return this.shouldProcess("error", errorKey, opts);
  }

  /**
   * Verifica se uma notificação deve ser mostrada
   */
  shouldShowNotification(
    notificationKey: string,
    options?: Partial<ThrottleOptions>
  ): boolean {
    const opts = { ...this.defaultOptions, ...options };
    return this.shouldProcess("notification", notificationKey, opts);
  }

  /**
   * Registra uma ocorrência de erro
   */
  recordError(errorKey: string): void {
    this.recordOccurrence("error", errorKey);
  }

  /**
   * Registra uma ocorrência de notificação
   */
  recordNotification(notificationKey: string): void {
    this.recordOccurrence("notification", notificationKey);
  }

  /**
   * Obtém estatísticas de throttling
   */
  getErrorStats(errorKey: string): ThrottleEntry | null {
    return this.errorCounts.get(errorKey) || null;
  }

  /**
   * Obtém estatísticas de notificações
   */
  getNotificationStats(notificationKey: string): ThrottleEntry | null {
    return this.notificationCounts.get(notificationKey) || null;
  }

  /**
   * Limpa entries antigas para economizar memória
   */
  cleanup(): void {
    const now = Date.now();
    const maxAge =
      this.defaultOptions.windowMs + (this.defaultOptions.cooldownMs || 0);

    this.cleanupMap(this.errorCounts, now, maxAge);
    this.cleanupMap(this.notificationCounts, now, maxAge);
  }

  /**
   * Reset completo do sistema de throttling
   */
  reset(): void {
    this.errorCounts.clear();
    this.notificationCounts.clear();
  }

  /**
   * Gera chave única para um erro baseado em suas características
   */
  generateErrorKey(error: any): string {
    // Usar combinação de tipo, status e mensagem para criar chave única
    const errorType = error?.response?.status || error?.code || "unknown";
    const errorMessage = error?.message || "unknown";
    const url = error?.config?.url || "unknown";

    // Normalizar URL para agrupar erros similares
    const normalizedUrl = url.replace(/\/\d+/g, "/:id").replace(/\?.*/, "");

    return `${errorType}-${normalizedUrl}-${errorMessage.substring(0, 50)}`;
  }

  /**
   * Gera chave única para notificação
   */
  generateNotificationKey(type: string, message: string): string {
    return `${type}-${message.substring(0, 100)}`;
  }

  private shouldProcess(
    type: "error" | "notification",
    key: string,
    options: ThrottleOptions
  ): boolean {
    const map = type === "error" ? this.errorCounts : this.notificationCounts;
    const entry = map.get(key);
    const now = Date.now();

    if (!entry) {
      // Primeira ocorrência - sempre permitir
      return true;
    }

    // Verificar se está no período de cooldown
    if (
      options.cooldownMs &&
      now - entry.lastOccurrence < options.cooldownMs &&
      entry.count >= options.maxOccurrences
    ) {
      return false;
    }

    // Verificar se está dentro da janela de tempo
    if (now - entry.firstOccurrence < options.windowMs) {
      // Dentro da janela - verificar limite
      return entry.count < options.maxOccurrences;
    } else {
      // Fora da janela - reset e permitir
      return true;
    }
  }

  private recordOccurrence(type: "error" | "notification", key: string): void {
    const map = type === "error" ? this.errorCounts : this.notificationCounts;
    const entry = map.get(key);
    const now = Date.now();

    if (!entry) {
      // Primeira ocorrência
      map.set(key, {
        count: 1,
        lastOccurrence: now,
        firstOccurrence: now,
      });
    } else {
      // Verificar se deve resetar o contador (nova janela)
      if (now - entry.firstOccurrence >= this.defaultOptions.windowMs) {
        map.set(key, {
          count: 1,
          lastOccurrence: now,
          firstOccurrence: now,
        });
      } else {
        // Incrementar contador
        entry.count++;
        entry.lastOccurrence = now;
      }
    }
  }

  private cleanupMap(
    map: Map<string, ThrottleEntry>,
    now: number,
    maxAge: number
  ): void {
    for (const [key, entry] of map.entries()) {
      if (now - entry.lastOccurrence > maxAge) {
        map.delete(key);
      }
    }
  }
}

// Instância singleton
export const errorThrottling = new ErrorThrottlingService();

// Configurações específicas para diferentes tipos de erro
export const ERROR_THROTTLE_CONFIGS = {
  NETWORK_ERROR: {
    maxOccurrences: 3,
    windowMs: 30000, // 30 segundos
    cooldownMs: 120000, // 2 minutos
  },
  API_ERROR: {
    maxOccurrences: 5,
    windowMs: 60000, // 1 minuto
    cooldownMs: 300000, // 5 minutos
  },
  VALIDATION_ERROR: {
    maxOccurrences: 10,
    windowMs: 60000, // 1 minuto
    cooldownMs: 60000, // 1 minuto
  },
  PERMISSION_ERROR: {
    maxOccurrences: 2,
    windowMs: 60000, // 1 minuto
    cooldownMs: 600000, // 10 minutos
  },
} as const;

// Hook para usar throttling em componentes React
export const useErrorThrottling = () => {
  const shouldLogError = (
    error: any,
    errorType?: keyof typeof ERROR_THROTTLE_CONFIGS
  ) => {
    const key = errorThrottling.generateErrorKey(error);
    const config = errorType ? ERROR_THROTTLE_CONFIGS[errorType] : undefined;

    if (errorThrottling.shouldLogError(key, config)) {
      errorThrottling.recordError(key);
      return true;
    }
    return false;
  };

  const shouldShowNotification = (type: string, message: string) => {
    const key = errorThrottling.generateNotificationKey(type, message);

    if (errorThrottling.shouldShowNotification(key)) {
      errorThrottling.recordNotification(key);
      return true;
    }
    return false;
  };

  return {
    shouldLogError,
    shouldShowNotification,
    getErrorStats: errorThrottling.getErrorStats.bind(errorThrottling),
    cleanup: errorThrottling.cleanup.bind(errorThrottling),
    reset: errorThrottling.reset.bind(errorThrottling),
  };
};

// Cleanup automático a cada 5 minutos
if (typeof window !== "undefined") {
  setInterval(() => {
    errorThrottling.cleanup();
  }, 300000); // 5 minutos
}

export default errorThrottling;
